import React, {
  useMemo,
  useState,
} from "react";

import AddPeoplePopup from "pages/community/People/AddPeoplePopup";
import AddPeopleFromDatabasePopup from "pages/community/People/AddPeopleFromDatabasePopup";

import ExpectedPeopleAttendance, {
  ExpectedPersonListItem,
} from "components/Interactions/ExpectedPeopleAttendance";

interface Props {
  addedPeople: ExpectedPersonListItem[];
  expectedPeople: ExpectedPersonListItem[];
  peopleList: ExpectedPersonListItem[];

  attendedPersonIds: number[];
  selectedPersonId?: number | null;

  loading?: boolean;
  attendanceLoadingPersonId?: number | null;
  addingDatabasePeople?: boolean;

  externalAccessToPeopleDatabase?: boolean;

  addPeople: (
    items: ExpectedPersonListItem[]
  ) => Promise<void> | void;

  setAttendance: (
    person: ExpectedPersonListItem,
    attended: boolean
  ) => Promise<void> | void;

  selectPerson: (
    person: ExpectedPersonListItem
  ) => Promise<void> | void;
}

const AttendanceSection: React.FC<Props> = ({
  addedPeople,
  expectedPeople,
  peopleList,
  attendedPersonIds,
  selectedPersonId,
  loading = false,
  attendanceLoadingPersonId = null,
  addingDatabasePeople = false,
  externalAccessToPeopleDatabase = false,
  addPeople,
  setAttendance,
  selectPerson,
}) => {
  const [
    addPersonOpen,
    setAddPersonOpen,
  ] = useState(false);

  const [
    addDatabasePeopleOpen,
    setAddDatabasePeopleOpen,
  ] = useState(false);

  const displayedPersonIds =
    useMemo(
      () => [
        ...addedPeople.map(
          (person) =>
            Number(person.id)
        ),

        ...expectedPeople.map(
          (person) =>
            Number(person.id)
        ),
      ],
      [
        addedPeople,
        expectedPeople,
      ]
    );

  const mapPersonToListItem = (
    person: any
  ): ExpectedPersonListItem => ({
    id: Number(person.id),

    primary: `${
      person.first_name ?? ""
    } ${
      person.last_name ?? ""
    }`.trim(),
  });

  const handleNewPersonSaved =
    async (newPerson: any) => {
      setAddPersonOpen(false);

      await addPeople([
        mapPersonToListItem(
          newPerson
        ),
      ]);
    };

  const handleAddDatabasePeople =
    async (
      people:
        ExpectedPersonListItem[]
    ) => {
      // Do not allow this action if
      // database access is disabled.
      if (
        !externalAccessToPeopleDatabase
      ) {
        return;
      }

      await addPeople(people);
    };

  const handleOpenDatabasePeople =
    () => {
      // Extra guard in case this
      // function is called elsewhere.
      if (
        !externalAccessToPeopleDatabase
      ) {
        return;
      }

      setAddDatabasePeopleOpen(
        true
      );
    };

  return (
    <>
      <ExpectedPeopleAttendance
        addedPeople={addedPeople}
        expectedPeople={
          expectedPeople
        }
        attendedPersonIds={
          attendedPersonIds
        }
        selectedPersonId={
          selectedPersonId
        }
        loading={loading}
        attendanceLoadingPersonId={
          attendanceLoadingPersonId
        }
        addingDatabasePeople={
          addingDatabasePeople
        }
        externalAccessToPeopleDatabase={
          externalAccessToPeopleDatabase
        }
        onAddNewPerson={() =>
          setAddPersonOpen(true)
        }
        onAddDatabasePeople={
          handleOpenDatabasePeople
        }
        onAttendanceChange={
          setAttendance
        }
        onSelectPerson={
          selectPerson
        }
      />

      <AddPeoplePopup
        open={addPersonOpen}
        onClose={() =>
          setAddPersonOpen(false)
        }
        onSaved={
          handleNewPersonSaved
        }
      />

      {externalAccessToPeopleDatabase && (
        <AddPeopleFromDatabasePopup
          open={
            addDatabasePeopleOpen
          }
          people={peopleList}
          excludedPersonIds={
            displayedPersonIds
          }
          loading={
            addingDatabasePeople
          }
          onClose={() =>
            setAddDatabasePeopleOpen(
              false
            )
          }
          onAdd={
            handleAddDatabasePeople
          }
        />
      )}
    </>
  );
};

export default AttendanceSection;